import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { BookingBreadcrumbs } from '@features/booking/components/BookingBreadcrumbs';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBookingFlow } from '@features/booking/flow';
import { useBooking } from '@features/booking/state';
import { useLanguage } from '@core/i18n';
import { sanitizeHtml } from '@shared/utils/sanitize';
import { useInactiveStepRecovery } from '@features/booking/hooks/useInactiveStepRecovery';

/**
 * Keywords to confirm early access, per language (case-insensitive).
 */
const EARLY_ACCESS_KEYWORDS: Record<string, string> = {
  es: 'acepto',
  en: 'agree',
  pt: 'concordo',
};

const EarlyAccessPage = () => {
  const { t, i18n } = useTranslation('booking');
  const { dateLocale } = useLanguage();
  const { goToNext } = useBookingFlow();
  // Stranded-guest guard: this page can be mounted on a step that is not in
  // activeSteps (stale bookmark, shared link, back/forward after the flow
  // moved on). See useInactiveStepRecovery.
  useInactiveStepRecovery('EARLY_ACCESS');
  const { selectedDate } = useBooking();

  const [keyword, setKeyword] = useState('');
  const [error, setError] = useState(false);

  const lang = i18n.language || 'en';
  const expectedKeyword = EARLY_ACCESS_KEYWORDS[lang] || EARLY_ACCESS_KEYWORDS.en;

  const dateLabel = selectedDate
    ? format(new Date(selectedDate + 'T12:00:00'), 'EEE d, MMM', { locale: dateLocale }).toUpperCase()
    : '';

  const handleConfirm = () => {
    if (keyword.trim().toLowerCase() === expectedKeyword) {
      setError(false);
      goToNext();
    } else {
      setError(true);
    }
  };

  return (
    <BookingPageLayout
      breadcrumbs={<BookingBreadcrumbs show={['DINERS', 'DATE', 'SERVICE']} />}
    >
      <div className="w-full px-6 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="font-display text-3xl sm:text-4xl text-foreground">
            {t('EARLY_ACCESS_VERY_EARLY')}
          </h1>
          {dateLabel && (
            <p className="text-xs font-medium tracking-wider uppercase text-foreground/60">
              {t('EARLY_ACCESS_NOT_OPEN')} {dateLabel}.
            </p>
          )}
        </div>
        <div className="bg-muted rounded-xl p-6 space-y-5 text-center">
          <h3 className="text-base font-bold tracking-wider uppercase text-foreground">
            {t('EARLY_ACCESS_REQUEST_EARLY_ACCESS')}
          </h3>
          <p className="text-sm text-foreground/80 leading-relaxed">
            {t('EARLY_ACCESS_NOT_OPEN_TEXT')}
          </p>
          <p className="text-xs font-semibold tracking-wider uppercase text-foreground/90">
            {t('EARLY_ACCESS_IS_NOT_A_RESERVATION')}
          </p>
        </div>

        <p
          className="text-sm text-foreground text-center [&_i]:italic [&_em]:italic [&_b]:font-semibold [&_strong]:font-semibold"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(t('EARLY_ACCESS_TEXT_TO_CONFIRM', { keyword: expectedKeyword })),
          }}
        />

        <div className="space-y-2">
          <Input
            data-testid="early-access-input"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              if (error) setError(false);
            }}
            placeholder={t('EARLY_ACCESS_COFIRM_INPUT_PLACEHOLDER')}
            className={`text-center rounded-full bg-card text-foreground placeholder:text-foreground/40 border border-border focus-visible:ring-1 focus-visible:ring-brand focus-visible:ring-offset-0 ${error ? 'border-red-500' : ''}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleConfirm();
            }}
          />
          {error && (
            <p className="text-sm text-red-600 text-center">
              {t('EARLY_ACCESS_CONFIRMATION_ERROR')}
            </p>
          )}
        </div>

        <Button
          data-testid="confirm-btn"
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-full w-full hover:bg-brand-dark shadow-md uppercase tracking-wider"
          onClick={handleConfirm}
          disabled={!keyword.trim()}
        >
          {t('common:SIGNIN_LABEL_SUBMIT')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default EarlyAccessPage;
